/**
 * Wavetable import for CookUp Core.
 *
 * Accepts:
 * - Serum/Vital-style wavetable WAVs: consecutive single cycles, usually
 *   2048 samples each, with an optional `clm ` chunk ("<!>2048 …") naming
 *   the frame size. Read losslessly from the file, never through
 *   decodeAudioData, which would resample and smear the frame boundaries.
 * - Any other audio (a vocal, a synth note, a field recording): the
 *   fundamental is detected and one cycle per frame is sliced across the
 *   file, each aligned to a rising zero crossing so neighbouring frames
 *   morph smoothly.
 *
 * Output is always `frames × 2048` samples, the engine's frame size.
 */

export const CORE_FRAME_SIZE = 2048;
export const MAX_IMPORT_FRAMES = 256;
const DEFAULT_SLICED_FRAMES = 64;

// ─── WAV parsing (raw, no resampling) ──────────────────────────────────────

const readTag = (view, offset) => String.fromCharCode(
  view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3)
);

/** Parse PCM 8/16/24/32-bit or float 32/64 WAV. Returns null if not a WAV. */
export function parseWav(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < 12 || readTag(view, 0) !== 'RIFF' || readTag(view, 8) !== 'WAVE') return null;
  let format = null;
  let data = null;
  let frameSize = null;
  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const id = readTag(view, offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      let code = view.getUint16(body, true);
      if (code === 0xfffe && size >= 26) code = view.getUint16(body + 24, true); // extensible
      format = {
        code,
        channels: view.getUint16(body + 2, true),
        sampleRate: view.getUint32(body + 4, true),
        bits: view.getUint16(body + 14, true)
      };
    } else if (id === 'data') {
      data = { offset: body, size: Math.min(size, view.byteLength - body) };
    } else if (id === 'clm ') {
      const text = Array.from({ length: Math.min(size, 64) }, (_, index) => String.fromCharCode(view.getUint8(body + index))).join('');
      const match = /<!>(\d+)/.exec(text);
      if (match) frameSize = Number(match[1]);
    }
    offset = body + size + (size % 2);
  }
  if (!format || !data || !format.channels) return null;
  const bytes = format.bits / 8;
  const frames = Math.floor(data.size / (bytes * format.channels));
  const channels = Array.from({ length: format.channels }, () => new Float32Array(frames));
  const read = (position) => {
    if (format.code === 3) return format.bits === 64 ? view.getFloat64(position, true) : view.getFloat32(position, true);
    if (format.bits === 8) return (view.getUint8(position) - 128) / 128;
    if (format.bits === 16) return view.getInt16(position, true) / 32768;
    if (format.bits === 24) {
      const value = view.getUint8(position) | (view.getUint8(position + 1) << 8) | (view.getInt8(position + 2) << 16);
      return value / 8388608;
    }
    return view.getInt32(position, true) / 2147483648;
  };
  if (![1, 3].includes(format.code)) return null;
  for (let frame = 0; frame < frames; frame += 1) {
    for (let channel = 0; channel < format.channels; channel += 1) {
      channels[channel][frame] = read(data.offset + (frame * format.channels + channel) * bytes);
    }
  }
  return { sampleRate: format.sampleRate, channels, frameSize };
}

const mixToMono = (channels) => {
  if (channels.length === 1) return channels[0];
  const mono = new Float32Array(channels[0].length);
  channels.forEach((channel) => channel.forEach((value, index) => { mono[index] += value / channels.length; }));
  return mono;
};

// ─── Resampling one cycle to 2048 samples ──────────────────────────────────

/**
 * Cubic (Catmull-Rom) resample of one cycle, [start, start + length), to
 * 2048 samples. `periodic` wraps inside the cycle (exact wavetable frames);
 * otherwise reads continue naturally into the neighbouring audio.
 */
function resampleCycle(source, start, length, target, targetOffset, periodic = false) {
  const origin = Math.floor(start);
  const at = periodic
    ? (index) => source[origin + ((((index - origin) % length) + length) % length)]
    : (index) => source[Math.min(source.length - 1, Math.max(0, index))];
  for (let n = 0; n < CORE_FRAME_SIZE; n += 1) {
    const position = start + (n / CORE_FRAME_SIZE) * length;
    const index = Math.floor(position);
    const t = position - index;
    const y0 = at(index - 1);
    const y1 = at(index);
    const y2 = at(index + 1);
    const y3 = at(index + 2);
    target[targetOffset + n] = y1 + 0.5 * t * (y2 - y0 + t * (2 * y0 - 5 * y1 + 4 * y2 - y3 + t * (3 * (y1 - y2) + y3 - y0)));
  }
}

// ─── Pitch detection ───────────────────────────────────────────────────────

/**
 * Fundamental period in samples (fractional), or null. YIN-style cumulative
 * mean normalised difference on a centred excerpt.
 */
export function detectPeriod(samples, sampleRate, { minHz = 30, maxHz = 2000 } = {}) {
  const minLag = Math.max(2, Math.floor(sampleRate / maxHz));
  const maxLag = Math.min(Math.floor(sampleRate / minHz), Math.floor(samples.length / 3));
  if (maxLag <= minLag) return null;
  const window = Math.min(samples.length - maxLag, maxLag * 3, Math.floor(sampleRate * 0.25));
  if (window < minLag * 2) return null;
  // Start where the signal is loudest-ish: skip leading silence.
  let start = 0;
  while (start < samples.length - window - maxLag && Math.abs(samples[start]) < 1e-3) start += 1;
  start = Math.min(start + Math.floor(sampleRate * 0.05), samples.length - window - maxLag);
  start = Math.max(0, start);
  const difference = new Float64Array(maxLag + 1);
  for (let lag = 1; lag <= maxLag; lag += 1) {
    let sum = 0;
    for (let n = 0; n < window; n += 1) {
      const delta = samples[start + n] - samples[start + n + lag];
      sum += delta * delta;
    }
    difference[lag] = sum;
  }
  let running = 0;
  let bestLag = -1;
  const normalised = new Float64Array(maxLag + 1);
  normalised[0] = 1;
  for (let lag = 1; lag <= maxLag; lag += 1) {
    running += difference[lag];
    normalised[lag] = running > 0 ? (difference[lag] * lag) / running : 1;
  }
  for (let lag = minLag; lag <= maxLag; lag += 1) {
    if (normalised[lag] < 0.15) {
      while (lag + 1 <= maxLag && normalised[lag + 1] < normalised[lag]) lag += 1;
      bestLag = lag;
      break;
    }
  }
  if (bestLag < 0) return null;
  // Parabolic refinement to a fractional period.
  const a = normalised[bestLag - 1] ?? normalised[bestLag];
  const b = normalised[bestLag];
  const c = normalised[bestLag + 1] ?? normalised[bestLag];
  const denominator = a - 2 * b + c;
  const shift = Math.abs(denominator) > 1e-12 ? 0.5 * (a - c) / denominator : 0;
  return bestLag + Math.max(-0.5, Math.min(0.5, shift));
}

const risingZeroNear = (samples, position, radius) => {
  const from = Math.max(1, Math.floor(position - radius));
  const to = Math.min(samples.length - 2, Math.ceil(position + radius));
  let best = null;
  for (let index = from; index <= to; index += 1) {
    if (samples[index - 1] <= 0 && samples[index] > 0) {
      const exact = index - 1 + samples[index - 1] / (samples[index - 1] - samples[index]);
      if (best === null || Math.abs(exact - position) < Math.abs(best - position)) best = exact;
    }
  }
  return best ?? position;
};

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Build table cycles from mono audio by slicing one detected cycle per frame
 * across the sound. Falls back to raw 2048-sample windows for unpitched
 * material.
 */
export function cyclesFromAudio(samples, sampleRate, { frames = DEFAULT_SLICED_FRAMES } = {}) {
  let first = 0;
  while (first < samples.length && Math.abs(samples[first]) < 1e-3) first += 1;
  let last = samples.length - 1;
  while (last > first && Math.abs(samples[last]) < 1e-3) last -= 1;
  const body = samples.subarray(first, last + 1);
  const period = detectPeriod(body, sampleRate);
  if (period && body.length > period * 2) {
    const count = Math.max(1, Math.min(frames, MAX_IMPORT_FRAMES, Math.floor(body.length / period) - 1));
    const cycles = new Float32Array(count * CORE_FRAME_SIZE);
    const span = body.length - period * 1.5;
    for (let frame = 0; frame < count; frame += 1) {
      const target = count === 1 ? period * 0.5 : period * 0.5 + (span - period * 0.5) * (frame / (count - 1));
      const start = risingZeroNear(body, target, period / 2);
      resampleCycle(body, start, period, cycles, frame * CORE_FRAME_SIZE);
    }
    return { cycles, frames: count, method: 'pitch', detectedHz: sampleRate / period };
  }
  const count = Math.max(1, Math.min(frames, Math.floor(body.length / CORE_FRAME_SIZE)));
  const cycles = new Float32Array(count * CORE_FRAME_SIZE);
  const step = count === 1 ? 0 : Math.floor((body.length - CORE_FRAME_SIZE) / (count - 1));
  for (let frame = 0; frame < count; frame += 1) {
    cycles.set(body.subarray(frame * step, frame * step + CORE_FRAME_SIZE), frame * CORE_FRAME_SIZE);
  }
  return { cycles, frames: count, method: 'windows', detectedHz: null };
}

/** Interpret parsed WAV data as a wavetable, or slice it as audio. */
export function cyclesFromWav(parsed, options = {}) {
  const mono = mixToMono(parsed.channels);
  const frameSize = parsed.frameSize
    || (mono.length >= CORE_FRAME_SIZE && mono.length % CORE_FRAME_SIZE === 0
      && mono.length / CORE_FRAME_SIZE <= MAX_IMPORT_FRAMES ? CORE_FRAME_SIZE : null);
  if (!frameSize) return cyclesFromAudio(mono, parsed.sampleRate, options);
  const count = Math.max(1, Math.min(MAX_IMPORT_FRAMES, Math.floor(mono.length / frameSize)));
  const cycles = new Float32Array(count * CORE_FRAME_SIZE);
  if (frameSize === CORE_FRAME_SIZE) {
    cycles.set(mono.subarray(0, count * CORE_FRAME_SIZE));
  } else {
    for (let frame = 0; frame < count; frame += 1) {
      resampleCycle(mono, frame * frameSize, frameSize, cycles, frame * CORE_FRAME_SIZE, true);
    }
  }
  return { cycles, frames: count, method: 'wavetable', detectedHz: null };
}

/**
 * Import a File/Blob. WAVs are read losslessly; anything else is decoded with
 * `decodeAudio(arrayBuffer)` (e.g. an AudioContext's decodeAudioData).
 */
export async function importWavetableFile(file, { decodeAudio, frames } = {}) {
  const bytes = await file.arrayBuffer();
  const parsed = parseWav(bytes);
  if (parsed) return { ...cyclesFromWav(parsed, { frames }), name: stripExtension(file.name) };
  if (typeof decodeAudio !== 'function') throw new Error('This file type needs audio decoding.');
  const buffer = await decodeAudio(bytes.slice(0));
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, index) => buffer.getChannelData(index));
  return { ...cyclesFromAudio(mixToMono(channels), buffer.sampleRate, { frames }), name: stripExtension(file.name) };
}

const stripExtension = (name) => String(name || 'Wavetable').replace(/\.[^.]+$/, '').slice(0, 60);

/** Stable content key for a table, shared by every engine that loads it. */
export function wavetableKey(cycles) {
  let hash = 2166136261;
  const stride = Math.max(1, Math.floor(cycles.length / 8192));
  for (let index = 0; index < cycles.length; index += stride) {
    hash ^= Math.round(cycles[index] * 32767) & 0xffff;
    hash = Math.imul(hash, 16777619);
  }
  return `table-${(hash >>> 0).toString(16)}-${cycles.length / CORE_FRAME_SIZE}`;
}

/**
 * 32-bit float mono WAV with a Serum-compatible `clm ` chunk, so an exported
 * CookUp table opens as a wavetable in Serum and Vital too.
 */
export function encodeWavetableWav(cycles, sampleRate = 48000) {
  const marker = `<!>${CORE_FRAME_SIZE} 00000000 wavetable (CookUp)`;
  const clmSize = marker.length + (marker.length % 2);
  const dataSize = cycles.length * 4;
  const total = 12 + (8 + 16) + (8 + clmSize) + (8 + dataSize);
  const buffer = new ArrayBuffer(total);
  const view = new DataView(buffer);
  let offset = 0;
  const tag = (text) => { for (let index = 0; index < 4; index += 1) view.setUint8(offset + index, text.charCodeAt(index)); offset += 4; };
  tag('RIFF'); view.setUint32(offset, total - 8, true); offset += 4; tag('WAVE');
  tag('fmt '); view.setUint32(offset, 16, true); offset += 4;
  view.setUint16(offset, 3, true); view.setUint16(offset + 2, 1, true);
  view.setUint32(offset + 4, sampleRate, true); view.setUint32(offset + 8, sampleRate * 4, true);
  view.setUint16(offset + 12, 4, true); view.setUint16(offset + 14, 32, true); offset += 16;
  tag('clm '); view.setUint32(offset, clmSize, true); offset += 4;
  for (let index = 0; index < clmSize; index += 1) view.setUint8(offset + index, index < marker.length ? marker.charCodeAt(index) : 0);
  offset += clmSize;
  tag('data'); view.setUint32(offset, dataSize, true); offset += 4;
  for (let index = 0; index < cycles.length; index += 1) view.setFloat32(offset + index * 4, cycles[index], true);
  return buffer;
}
